/**
 * <gallery-tabs> renders a row of tabs (e.g. Overview / Closer Look / App) above the
 * product media thumbnails. Each tab is tied to one or more media positions (1-based,
 * matching the order of images in the product's Media list) -- including the
 * featured/hero thumbnail (always shown first/leftmost, whichever image that is for
 * the selected variant): it keeps its own real raw position and is dimmed/highlighted
 * by the active tab exactly like any other thumbnail, no special-casing.
 *
 * Clicking a tab:
 *  - marks the tab as active
 *  - dims every thumbnail that isn't assigned to that tab (thumbnails belonging to the
 *    active tab stay at full opacity)
 *  - scrolls the gallery to the tab's first assigned image
 *
 * The active tab also stays in sync when the shopper swipes/uses the arrows on the main
 * image instead of clicking a tab.
 *
 * Note: this deliberately does NOT rely on the theme's own `.slider__active` bookkeeping
 * (added by <slider-component> in global.js). That code computes the active thumbnail
 * via a jQuery `:eq(page - 1)` index alongside an `if (position)` check, which silently
 * breaks whenever `position` is `0` (i.e. the very first thumbnail) since `0` is falsy in
 * JS -- clicking/selecting the first image doesn't move the indicator. Relying on that
 * class for tab sync caused tabs to "snap back" to the wrong tab. Instead, we track and
 * mark the active thumbnail ourselves from the main slider's actual scroll position.
 */
class GalleryTabs extends HTMLElement {
  constructor() {
    super();

    // Defensive cleanup for the whole-page horizontal shift this gallery used to cause
    // (fixed by switching scrollToPosition() off scrollIntoView() -- see below). Browsers
    // can still *restore* a stray non-zero horizontal scroll position on a plain reload of
    // the same URL from before that fix, so force it back to 0 and stop them doing that
    // again. Harmless no-op once nothing is scrolling the page sideways anymore.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    if ((document.documentElement.scrollLeft || document.body.scrollLeft) > 0) {
      window.scrollTo({ left: 0, top: window.scrollY });
    }

    this.tabs = Array.from(this.querySelectorAll('[data-gallery-tab]'));
    this.mediaWrapper = this.closest('.product__media-wrapper') || document;
    this.mainSlider = this.mediaWrapper.querySelector('#thumbnail-main-slider');
    this.bottomSlider = this.mediaWrapper.querySelector('#thumbnail-bottom-slider');
    this.isNavigatingToTab = false;
    this._scrollSyncTimeout = null;
    this._resumeSyncTimeout = null;
    this._guardMaxTimeout = null;
    this._buttonSyncTimeout = null;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick.bind(this));
    });

    // ARIA tablist pattern: one Tab stop for the row, ←/→ (and Home/End) move between tabs.
    this.addEventListener('keydown', this.onTabKeydown.bind(this));

    this.scroller = this.closest('[data-gallery-tabs-scroller]');
    this.observeTabRowOverflow();

    const initialTab = this.tabs.find((tab) => tab.classList.contains('is-active')) || this.tabs[0];
    if (initialTab) this.updateThumbnailEmphasis(this.getPositions(initialTab));

    this.observeMainSliderScroll();
    this.observeSliderButtons();

    // Nudge the bottom thumbnail strip when an edge thumbnail is clicked (see
    // onBottomThumbClick). Capture phase so we set our guard *before* global.js's own
    // per-thumbnail click handler (bound on each <li>) runs -- its slider-component.update()
    // then bails on `data-gallery-tab-scrolling` and won't fight our nudge, while its main
    // slider scroll (the actual hero swap) still happens as normal.
    this.bottomSlider?.addEventListener('click', this.onBottomThumbClick.bind(this), true);

    // global.js's VariantSelects only ever swaps the photo shown in the fixed hero slot
    // (position 1, e.g. a colour/size swatch) -- it never touches which tab/scroll position
    // is currently on screen. So if the shopper had scrolled to another tab (e.g. "Closer
    // Look") and then changes an option, the hero slot's content changes off-screen and it
    // *looks* like nothing happened. Jump back to whichever tab contains position 1 and
    // scroll to it so the new photo is actually visible.
    document.addEventListener('gallery:hero-updated', this.goToHeroTab.bind(this));
  }

  goToHeroTab() {
    const heroTab = this.tabs.find((tab) => this.getPositions(tab).includes('1'));
    if (!heroTab) return;

    this.activateTabAndScroll(heroTab, this.getPositions(heroTab));
  }

  getPositions(tab) {
    return (tab.getAttribute('data-positions') || '')
      .split(',')
      .map((position) => position.trim())
      .filter(Boolean);
  }

  getThumbPositions(thumb) {
    // Hidden natural-position duplicate of whatever is currently shown in the fixed hero
    // slot (position 1) -- see global.js's _syncHeroSlot(). It must stay out of tab
    // matching/scrolling entirely, since the *visible* representative is the hero slot.
    if (thumb.classList.contains('gallery-hero-duplicate')) return [];

    return (thumb.getAttribute('data-media-position') || '')
      .split(',')
      .map((position) => position.trim())
      .filter(Boolean);
  }

  onTabKeydown(event) {
    const current = this.tabs.indexOf(event.target.closest('[data-gallery-tab]'));
    if (current === -1) return;

    let next = null;
    if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    const tab = this.tabs[next];
    tab.focus();
    this.activateTabAndScroll(tab, this.getPositions(tab));
  }

  // Mobile: the tab row scrolls sideways when the tabs don't fit. Fades the edge that has
  // more tabs behind it (see .has-more-start / .has-more-end in the CSS).
  observeTabRowOverflow() {
    if (!this.scroller) return;

    const update = () => {
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      this.scroller.classList.toggle('has-more-start', maxScroll > 1 && this.scroller.scrollLeft > 1);
      this.scroller.classList.toggle('has-more-end', maxScroll > 1 && this.scroller.scrollLeft < maxScroll - 1);
    };

    this.scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  // The ‹ › buttons on the main image. The scroll listener below normally catches the move,
  // but on desktop the slider can land without a scroll event reaching us, so check again
  // once the move has had time to finish.
  observeSliderButtons() {
    if (!this.mainSlider) return;
    const sliderComponent = this.mainSlider.closest('slider-component') || this.mediaWrapper;

    sliderComponent.querySelectorAll('.slider-button--prev, .slider-button--next').forEach((button) => {
      if (button.closest('.product-thumbnails-section')) return;
      button.addEventListener('click', () => {
        window.clearTimeout(this._buttonSyncTimeout);
        this._buttonSyncTimeout = window.setTimeout(() => {
          if (!this.isNavigatingToTab) this.syncFromVisibleMedia();
        }, 450);
      });
    });
  }

  onTabClick(event) {
    const tab = event.currentTarget;
    const positions = this.getPositions(tab);
    if (!positions.length) return;

    this.activateTabAndScroll(tab, positions);
  }

  // Shared by a manual tab click and goToHeroTab() (triggered when a variant change swaps
  // the hero photo while a different tab is on screen): marks `tab` active, dims/highlights
  // thumbnails accordingly, and smooth-scrolls both sliders to the first of `positions`.
  activateTabAndScroll(tab, positions) {
    this.setActiveTab(tab);
    this.updateThumbnailEmphasis(positions);

    // Tell <slider-component> (global.js) to stand down while we drive the scroll
    // ourselves -- its own `scroll` listener recomputes a "page" from raw DOM index/
    // scrollLeft math and re-scrolls the bottom thumbnail row to that (now wrong, since
    // it doesn't know about tab positions or hidden/duplicate thumbnails) position at the
    // same time we do, which visually looks like the gallery "swipes" itself right after
    // a tab click. See its `update()` guard in global.js.
    document.documentElement.setAttribute('data-gallery-tab-scrolling', 'true');

    // Both sliders use CSS `scroll-snap-type: x mandatory`. Jumping several thumbnails at
    // once (e.g. Overview -> Closer Look) makes the browser "correct" our target position
    // to the nearest valid snap point in a *separate* animation right after ours -- visibly
    // looking like an extra, uncontrolled swipe to the right. Suspending snapping for the
    // duration of our own smooth-scroll avoids that fight; see the matching CSS rule.
    this.mainSlider?.classList.add('gallery-tab-scrolling');
    this.bottomSlider?.classList.add('gallery-tab-scrolling');

    this.scrollToPosition(positions);

    // Ignore scroll-spy updates while our own smooth-scroll is still animating,
    // otherwise it can momentarily read a mid-scroll position and fight the click.
    //
    // The guard is released when scrolling has been idle for a moment (see
    // _scheduleGuardRelease), NOT after a fixed delay. A fixed timeout raced with the
    // smooth-scroll for far tabs (e.g. "Lifestyle" near the end of the list): if it fired
    // mid-scroll, global.js's slider-component.update() would run on the still-arriving
    // scroll events and call sliderMove(), which re-scrolls the bottom strip so the active
    // thumbnail sits near the RIGHT edge (`newCurrentItem * width - scrollR`) -- making the
    // just-selected first image jump to the end of the view. Holding the guard until the
    // main slider actually stops (it rests exactly on a `start` snap point, so removing the
    // class triggers no further snap/scroll) keeps update() suppressed for the whole trip.
    this.isNavigatingToTab = true;
    window.clearTimeout(this._guardMaxTimeout);
    this._guardMaxTimeout = window.setTimeout(() => this.releaseScrollGuard(), 1500);
    this.scheduleGuardRelease();
  }

  // Debounced release: each scroll event from either slider (see the scroll listeners)
  // pushes this out, so the guard only lifts once scrolling has settled. Also armed once
  // up-front in case the target is already on screen and no scroll event ever fires.
  scheduleGuardRelease() {
    window.clearTimeout(this._resumeSyncTimeout);
    this._resumeSyncTimeout = window.setTimeout(() => this.releaseScrollGuard(), 200);
  }

  releaseScrollGuard() {
    window.clearTimeout(this._resumeSyncTimeout);
    window.clearTimeout(this._guardMaxTimeout);
    this.isNavigatingToTab = false;
    document.documentElement.removeAttribute('data-gallery-tab-scrolling');
    this.mainSlider?.classList.remove('gallery-tab-scrolling');
    this.bottomSlider?.classList.remove('gallery-tab-scrolling');
  }

  setActiveTab(activeTab) {
    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive);
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  updateThumbnailEmphasis(positions) {
    this.mediaWrapper.querySelectorAll('[data-media-position]').forEach((thumb) => {
      const belongsToActiveTab = this.getThumbPositions(thumb).some((position) => positions.includes(position));
      thumb.classList.toggle('gallery-tab-dimmed', !belongsToActiveTab);
    });
  }

  // Marks the thumbnail(s) matching `positions` as active in both the main and bottom
  // sliders, by direct element reference -- not by recomputing an index, which is what
  // trips up the theme's own (buggy) equivalent.
  markActiveThumbnail(positions) {
    const positionList = Array.isArray(positions) ? positions : [positions];
    [this.mainSlider, this.bottomSlider].forEach((list) => {
      if (!list) return;
      list.querySelectorAll('[data-media-position]').forEach((item) => {
        const isActive = this.getThumbPositions(item).some((position) => positionList.includes(position));
        item.classList.toggle('slider__active', isActive);
      });
    });
  }

  // Accepts either a single position or an ordered list of positions, and jumps to the
  // first one that actually has a matching thumbnail (some products can have a position
  // with no rendered thumbnail, e.g. a photo that's currently hidden behind a variant).
  scrollToPosition(positions) {
    const positionList = Array.isArray(positions) ? positions : [positions];

    for (const position of positionList) {
      const mainThumb = Array.from(this.mainSlider?.querySelectorAll('[data-media-position]') || []).find((el) =>
        this.getThumbPositions(el).includes(position)
      );
      const bottomThumb = Array.from(this.bottomSlider?.querySelectorAll('[data-media-position]') || []).find((el) =>
        this.getThumbPositions(el).includes(position)
      );
      if (!mainThumb && !bottomThumb) continue;

      // The main slider jumps straight to the tab's first photo: smooth-scrolling it across
      // a far tab plays every big photo in between, which reads as the gallery flicking
      // through itself. The thumbnail strip still glides, so the move stays easy to follow.
      this._scrollThumbIntoView(this.mainSlider, mainThumb, 'start', 'instant');
      this._scrollThumbIntoView(this.bottomSlider, bottomThumb, 'start');
      this.markActiveThumbnail(this.getThumbPositions(mainThumb || bottomThumb));
      return;
    }
  }

  // Scrolls `container` (never any ancestor of it, unlike `element.scrollIntoView()`) so
  // `element` sits at its start/center edge. Jumping to a thumbnail near the end of the
  // list (e.g. a later tab) can ask for more scrollLeft than the container actually has
  // left to give -- `scrollIntoView()` in that situation can hand the remaining scroll
  // "debt" off to an ancestor (up to the page itself), which is what made the whole
  // storefront shift sideways when clicking a tab. Computing and clamping the target
  // ourselves, then calling `scrollTo()` directly on the container, keeps the scroll fully
  // contained to that one slider.
  _scrollThumbIntoView(container, element, align, behavior = 'smooth') {
    if (!container || !element) return;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    let target = container.scrollLeft + (elementRect.left - containerRect.left);

    if (align === 'center') {
      target -= (container.clientWidth - element.clientWidth) / 2;
    } else if (align === 'end') {
      target -= container.clientWidth - element.clientWidth;
    }

    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    target = Math.min(Math.max(target, 0), maxScrollLeft);

    container.scrollTo({ left: target, behavior });
  }

  // How much of `element` is currently within `container`'s viewport, as a 0..1 fraction of
  // the element's own width. Used to tell whether a thumbnail sits at the visible edge.
  visibleRatio(container, element) {
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const visible =
      Math.max(0, Math.min(elementRect.right, containerRect.right) - Math.max(elementRect.left, containerRect.left));
    return elementRect.width ? visible / elementRect.width : 0;
  }

  // Clicking a thumbnail at the visible edge of the bottom strip nudges the strip so the
  // neighbour just past that edge becomes fully visible -- so repeated clicks keep walking
  // through the gallery instead of getting stuck against the same edge. A click on an
  // already fully-visible thumbnail needs no nudge, but must still stand global.js down
  // (see below) so it doesn't yank the strip to the end.
  onBottomThumbClick(event) {
    if (!this.bottomSlider) return;
    const clicked = event.target.closest('[data-media-position]');
    if (!clicked || !this.bottomSlider.contains(clicked)) return;

    const thumbs = Array.from(this.bottomSlider.querySelectorAll('[data-media-position]')).filter(
      (el) => !el.classList.contains('gallery-hero-duplicate') && el.offsetParent !== null
    );
    const index = thumbs.indexOf(clicked);
    if (index === -1) return;

    const prev = thumbs[index - 1];
    const next = thumbs[index + 1];
    const FULLY_VISIBLE = 0.98;
    const clickedRatio = this.visibleRatio(this.bottomSlider, clicked);

    let targetThumb = null;
    let align = null;

    if (clickedRatio >= FULLY_VISIBLE && next && this.visibleRatio(this.bottomSlider, next) < FULLY_VISIBLE) {
      // Last fully-visible thumb clicked: bring the next one fully into view at the right edge.
      targetThumb = next;
      align = 'end';
    } else if (clickedRatio >= FULLY_VISIBLE && prev && this.visibleRatio(this.bottomSlider, prev) < FULLY_VISIBLE) {
      // First fully-visible thumb clicked: bring the previous one fully into view at the left edge.
      targetThumb = prev;
      align = 'start';
    } else if (clickedRatio < FULLY_VISIBLE) {
      // A partly-clipped thumb (e.g. the peeking sliver) was clicked: reveal it fully toward
      // whichever edge it's clipped against.
      const containerRect = this.bottomSlider.getBoundingClientRect();
      const clickedRect = clicked.getBoundingClientRect();
      targetThumb = clicked;
      align = clickedRect.left < containerRect.left ? 'start' : 'end';
    }

    // Guarding below suppresses the scroll-spy (syncFromVisibleMedia) that would otherwise
    // keep the active tab/highlight in step with the hero swap, so do that ourselves here.
    this.syncActiveFromThumb(clicked);

    // Stand global.js down (see the capture-phase listener that calls this) and suspend snap
    // on the strip while we drive this single nudge -- mirrors activateTabAndScroll's guard.
    //
    // This must happen for EVERY thumbnail click, not just the edge nudges above. When the
    // clicked thumb is already fully visible we don't scroll the strip ourselves, but if we
    // returned here without the guard, global.js's per-thumbnail handler (smallIndex ->
    // update -> sliderMove) would re-scroll the strip to pin the clicked thumb against the
    // right edge -- the "clicking Walnut jumps the gallery to the end" the review flagged.
    document.documentElement.setAttribute('data-gallery-tab-scrolling', 'true');
    this.bottomSlider.classList.add('gallery-tab-scrolling');
    if (targetThumb) this._scrollThumbIntoView(this.bottomSlider, targetThumb, align);

    this.isNavigatingToTab = true;
    window.clearTimeout(this._guardMaxTimeout);
    this._guardMaxTimeout = window.setTimeout(() => this.releaseScrollGuard(), 1500);
    this.scheduleGuardRelease();
  }

  // Marks the clicked thumbnail active and, if it belongs to a different tab, switches to it
  // -- the same bookkeeping syncFromVisibleMedia does on manual navigation.
  syncActiveFromThumb(thumb) {
    const positions = this.getThumbPositions(thumb);
    if (!positions.length) return;

    this.markActiveThumbnail(positions);

    const matchingTab = this.tabs.find((tab) =>
      this.getPositions(tab).some((position) => positions.includes(position))
    );
    if (matchingTab && !matchingTab.classList.contains('is-active')) {
      this.setActiveTab(matchingTab);
      this.updateThumbnailEmphasis(this.getPositions(matchingTab));
    }
  }

  // Keeps the active tab in sync when the shopper navigates the gallery manually
  // (swipe, arrow buttons, or clicking a thumbnail directly) instead of using the tabs.
  // Determined directly from the main slider's scroll position, not from the theme's
  // `.slider__active` class (see class-level comment for why).
  observeMainSliderScroll() {
    if (!this.mainSlider) return;

    this.mainSlider.addEventListener('scroll', () => {
      // While driving a tab scroll, every scroll tick means we're still moving -- keep the
      // guard alive so global.js can't reposition the bottom strip until we've fully landed.
      if (this.isNavigatingToTab) {
        this.scheduleGuardRelease();
        return;
      }
      window.clearTimeout(this._scrollSyncTimeout);
      this._scrollSyncTimeout = window.setTimeout(() => this.syncFromVisibleMedia(), 120);
    });

    // The bottom strip scrolls independently of the main slider during a tab click, so it
    // also has to keep the guard alive until it settles.
    this.bottomSlider?.addEventListener('scroll', () => {
      if (this.isNavigatingToTab) this.scheduleGuardRelease();
    });
  }

  syncFromVisibleMedia() {
    // Exclude hidden natural-position duplicates (see getThumbPositions) -- being
    // `display: none`, they'd otherwise report `offsetLeft === 0` and could wrongly win
    // as the "closest" item whenever the slider is scrolled near the start.
    const items = Array.from(this.mainSlider.querySelectorAll('[data-media-position]')).filter(
      (item) => !item.classList.contains('gallery-hero-duplicate')
    );
    if (!items.length) return;

    const scrollLeft = this.mainSlider.scrollLeft;
    let closestItem = items[0];
    let closestDistance = Infinity;

    items.forEach((item) => {
      const distance = Math.abs(item.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = item;
      }
    });

    const currentPositions = this.getThumbPositions(closestItem);
    const matchingTab = this.tabs.find((tab) =>
      this.getPositions(tab).some((position) => currentPositions.includes(position))
    );

    this.markActiveThumbnail(currentPositions);

    if (!matchingTab || matchingTab.classList.contains('is-active')) return;

    this.setActiveTab(matchingTab);
    this.updateThumbnailEmphasis(this.getPositions(matchingTab));
  }
}

if (!customElements.get('gallery-tabs')) {
  customElements.define('gallery-tabs', GalleryTabs);
}
